import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { COMPANY, LOGOS } from "@/data/site";
import CPMark from "@/components/CPMark";

const NAV = [
  { label: "Firm", to: "/about", children: [
    { slug: "about",           title: "About" },
    { slug: "our-story",       title: "Our Story" },
    { slug: "team",            title: "Leadership" },
    { slug: "talent-network",  title: "Talent Network" },
  ]},
  { label: "For Organizations", to: "/services", children: [
    { slug: "services",             title: "Consulting Services" },
    { slug: "solutions",            title: "Solutions Catalog" },
    { slug: "workforce-solutions",  title: "Workforce Solutions" },
    { slug: "government",           title: "Government & Procurement" },
    { slug: "industries",           title: "Industries" },
    { slug: "case-studies",         title: "Case Studies" },
    { slug: "business-assessment",  title: "Business Assessment" },
  ]},
  { label: "For Professionals", to: "/career-services", children: [
    { slug: "career-services",              title: "Career Services" },
    { slug: "career-readiness-assessment",  title: "Career Readiness Assessment" },
  ]},
  { label: "Insights & Resources", to: "/resources", children: [
    { slug: "blog",       title: "Blog" },
    { slug: "resources",  title: "Insights & Resources" },
    { slug: "faq",        title: "FAQ" },
  ]},
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setOpenMenu(null); }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-white/85 border-b border-slate-200/70 shadow-[0_8px_32px_rgba(15,45,82,0.06)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 flex items-center justify-between h-28">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <CPMark className="w-20 h-20 transition-transform duration-500 group-hover:scale-105" />
          <span className="hidden sm:flex flex-col leading-none">
            <span className={`font-display font-800 text-lg tracking-tightest transition-colors ${scrolled ? "text-navy" : "text-white"}`}>
              CONSILIUM <span className="text-gold">PRO</span>
            </span>
            <span className={`overline text-[9px] mt-1 transition-colors ${scrolled ? "text-gray-500" : "text-white/60"}`}>Workforce Solutions</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((item) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => item.children && setOpenMenu(item.label)}
              onMouseLeave={() => item.children && setOpenMenu(null)}
            >
              <Link
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={`font-body text-sm font-500 flex items-center gap-1 link-underline transition-colors ${
                  location.pathname === item.to ? "text-gold" : scrolled ? "text-navy hover:text-gold" : "text-white hover:text-gold"
                }`}
              >
                {item.label}
                {item.children && <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.5} />}
              </Link>

              <AnimatePresence>
                {item.children && openMenu === item.label && (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 12 }}
                    transition={{ duration: 0.25 }}
                    className={`absolute left-1/2 -translate-x-1/2 top-full pt-5 ${item.label === "Services" ? "w-[420px]" : "w-[240px]"}`}
                  >
                    <div className="bg-white border border-slate-200 shadow-xl p-3">
                      {item.children.map((s) => (
                        <button
                          key={s.slug}
                          data-testid={`nav-service-${s.slug}`}
                          onClick={() => navigate(item.label === "Services" ? `/services/${s.slug}` : `/${s.slug}`)}
                          className="block w-full text-left px-4 py-3 hover:bg-lightgray transition-colors group"
                        >
                          <span className="font-display font-600 text-sm text-navy group-hover:text-gold transition-colors">{s.title}</span>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4 xl:gap-5 xl:pl-6">
          <a href={COMPANY.phoneHref} data-testid="nav-phone" className={`hidden xl:flex items-center gap-2 shrink-0 text-sm font-500 whitespace-nowrap transition-colors hover:text-gold ${scrolled ? "text-navy" : "text-white"}`}>
            <Phone className="w-4 h-4 shrink-0" strokeWidth={1.5} /> {COMPANY.phone}
          </a>
          <Link
            to="/business-assessment"
            data-testid="nav-cta-assessment"
            className={`text-sm font-600 px-5 py-3 border transition-colors duration-300 ${scrolled ? "border-navy text-navy hover:bg-navy hover:text-white" : "border-white text-white hover:bg-white hover:text-navy"}`}
          >
            Take the Assessment
          </Link>
          <Link
            to="/contact"
            data-testid="nav-cta"
            className={`text-sm font-600 px-5 py-3 transition-colors duration-300 ${scrolled ? "bg-navy text-white hover:bg-gold hover:text-navy" : "bg-gold text-navy hover:bg-white"}`}
          >
            Schedule a Consultation
          </Link>
        </div>

        <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} className={`lg:hidden p-2 transition-colors ${scrolled || open ? "text-navy" : "text-white"}`}>
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-slate-200 overflow-hidden"
          >
            <div className="px-5 py-6 flex flex-col gap-1">
              {NAV.map((item) => (
                <div key={item.label} className="border-b border-slate-100">
                  <Link
                    to={item.to}
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                    className="block py-3 font-display font-600 text-navy"
                  >
                    {item.label}
                  </Link>
                  {item.children && item.label !== "Services" && (
                    <div className="pb-2 pl-4 flex flex-col gap-1">
                      {item.children.map((c) => (
                        <Link key={c.slug} to={`/${c.slug}`} className="block py-1.5 text-sm text-navy/70">{c.title}</Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <a href={COMPANY.phoneHref} className="py-3 text-navy flex items-center gap-2"><Phone className="w-4 h-4" /> {COMPANY.phone}</a>
              <Link to="/business-assessment" className="mt-3 border border-navy text-navy text-center font-600 px-6 py-4">Take the Assessment</Link>
              <Link to="/contact" className="mt-2 bg-navy text-white text-center font-600 px-6 py-4">Schedule a Consultation</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
